import type { TrackingEvent } from '../types/tracking.types';
import { calculateNumItems } from '../utils/tracking.utils';

/**
 * Mapeamento de eventos para TikTok Pixel
 */
const TTK_EVENT_MAPPING: Record<string, string> = {
  'begin_checkout': 'InitiateCheckout',
  // add_shipping_info: não existe como evento padrão TikTok — omitido
  'add_payment_info': 'AddPaymentInfo',
  'purchase': 'Purchase', // renomeado de CompletePayment (deprecated maio/2025)
};

/**
 * Converte items do ecommerce para formato contents do TikTok
 */
const buildTikTokContents = (event: TrackingEvent): Array<{ content_id: string; quantity: number; price?: number; content_name?: string }> => {
  const items = event.ecommerce?.items || [];
  return items.map((item) => ({
    content_id: String(item.item_id),
    quantity: item.quantity || 1,
    price: item.price,
    content_name: item.item_name,
  }));
};

/**
 * Envia evento para TikTok Pixel via ttq.track
 */
export const sendToTikTok = (
  pixelCode: string,
  eventName: string,
  event: TrackingEvent,
  universalEventId: string
): void => {
  if (!pixelCode || !(window as any).ttq) return;

  try {
    const ttEventName = TTK_EVENT_MAPPING[eventName] ?? eventName;
    if (!ttEventName) return;

    const ttq = (window as any).ttq;
    const api = typeof ttq?.instance === 'function' ? ttq.instance(pixelCode) : ttq;

    const contents = buildTikTokContents(event);
    const contentIds = event.ecommerce?.items?.map((item) => String(item.item_id)) || [];
    const value = Number((event.ecommerce?.value ?? 0).toFixed(2));
    const currency = event.ecommerce?.currency || 'BRL';

    const properties: Record<string, any> = {
      content_type: 'product',
      content_ids: contentIds,
      contents,
      currency,
      value,
      num_items: calculateNumItems(event.ecommerce?.items || []),
    };

    if (eventName === 'purchase' && (event as any).ecommerce?.transaction_id) {
      properties.order_id = (event as any).ecommerce.transaction_id;
    }

    const options = { event_id: universalEventId };
    // multi-pixel safe: track apenas para o pixel informado
    api.track(ttEventName, properties, options);
  } catch (error) {
    console.error(`❌ TikTok: Erro ao enviar evento para ${pixelCode}:`, error);
  }
};

/**
 * Envia PageView para TikTok Pixel
 * universalEventId reservado para futura deduplicação com Events API
 */
export const sendPageViewToTikTok = (pixelCode: string, _universalEventId: string): void => {
  if (!pixelCode || !(window as any).ttq) return;

  try {
    const ttq = (window as any).ttq;
    const api = typeof ttq?.instance === 'function' ? ttq.instance(pixelCode) : ttq;
    // multi-pixel safe: page apenas para o pixel informado
    api.page();
  } catch (error) {
    console.error(`❌ TikTok: Erro ao enviar PageView para ${pixelCode}:`, error);
  }
};
